/**
 * Compresses an image file to reduce size for upload (e.g. mobile camera photos).
 * Resizes if dimensions exceed max, reduces JPEG quality.
 * @param {File} file - Image file to compress
 * @param {Object} options - { maxDimension: 1920, maxSizeMB: 3, quality: 0.65 }
 * @returns {Promise<File>} Compressed file
 */
export const compressImageFile = (file, options = {}) => {
  const { maxDimension = 1920, maxSizeMB = 3, quality = 0.65 } = options;
  const maxBytes = maxSizeMB * 1024 * 1024;

  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return Promise.resolve(file);
  }
  if (file.size <= maxBytes) {
    return Promise.resolve(file);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        } else {
          width = Math.round((width * maxDimension) / height);
          height = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const tryQuality = (q, retries = 2) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            if (blob.size > maxBytes && retries > 0 && q > 0.4) {
              tryQuality(Math.max(0.4, q - 0.15), retries - 1);
              return;
            }
            const name = file.name.replace(/\.[^.]+$/, '') || 'image';
            resolve(new File([blob], `${name}.jpg`, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          q
        );
      };
      tryQuality(quality);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
};
